import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { TeamAccessPending, TeamUnpaid } from '@labradorsports/components';

import { NavigateWithQuery, NavLinkWithQuery } from '../../../shared/structural/index.js';
import { Selectors, routerActions, useFetchTeamsQuery } from '../../../store/index.js';
import { useDispatcher } from '../../hooks/index.js';

interface Props {
    requireSubscription?: boolean;
    excludePlans?: string[];
    fallback?: any;
    requireEditRights?: boolean;
}

const PrivateRoute: FC<Props> = ({ children, requireSubscription, excludePlans = [], requireEditRights, fallback }) => {
    const setDeeplink = useDispatcher(routerActions.SetDeeplink);

    const user = useSelector(Selectors.user);
    const profileLoaded = useSelector(Selectors.profileLoaded);
    const activeTeam = useSelector(Selectors.activeTeam);
    const currentTeam = useSelector(Selectors.currentTeam);
    const teamEditable = useSelector(Selectors.teamEditable);
    const teamViewable = useSelector(Selectors.teamViewable);

    useFetchTeamsQuery();

    const location = useLocation();

    if (
        // No auth event yet
        user === undefined ||
        // User logged in, profile not loaded yet
        (user !== null && (!profileLoaded || (activeTeam && !currentTeam)))
    ) {
        return null;
    }

    const { pathname, search } = location;

    // User is trying to view a private route without being logged in - redirect them to the login page
    if (user === null) {
        // Save original destination path
        // Delay avoids conflict with router-state-sync and the redirect
        setTimeout(() => {
            setDeeplink(encodeURIComponent(`${pathname}${search}`));
        }, 100);

        return <NavigateWithQuery to="/login" />;
    }

    // User can't edit
    if (requireEditRights && !teamEditable) {
        return <NavigateWithQuery to="/feed" />;
    }

    const renderContent = (children) => (
        <div className="page-layout no-left">
            <div className="right-panel">
                <div className="panel-container">
                    <div className="panel-content">
                        <div className="page-content">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Team subscription expired
    if (requireSubscription) {
        if (!currentTeam) {
            // If fallback UI is specified, render it
            if (fallback) {
                return fallback;
            }

            // Otherwise, redirect to home
            return <NavigateWithQuery to="/feed" />;
        }

        if (currentTeam.pending) {
            return renderContent(<TeamAccessPending />);
        }

        if (!teamViewable) {
            return renderContent(<TeamUnpaid />);
        }

        if (excludePlans.includes(currentTeam.plan)) {
            return renderContent(
                <div className="alert alert-warning">
                    This team&apos;s subscription does not include this feature.{' '}
                    {currentTeam.ownerId === user.uid ? (
                        <span>
                            Visit the{' '}
                            <NavLinkWithQuery to="/profile/subscriptions/manage">MANAGE TEAMS</NavLinkWithQuery> page to
                            upgrade this team.
                        </span>
                    ) : (
                        'Talk to your team admin to upgrade your team and have this feature enabled.'
                    )}
                </div>
            );
        }
    }

    // All checks passed
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

export default PrivateRoute;
